/**
 * ComparisonScoringConfigForm Component
 *
 * Formik form for creating/editing comparison scoring config (per rule).
 * Includes category weights, missing/mismatched factors, severity mappings, and custom SQL override.
 *
 * ADD this file to: datatool_fe-QA/src/components/Scoring/ComparisonScoringConfigForm.tsx
 */
import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorIcon from "@mui/icons-material/ErrorOutlined";
import { comparisonScoringConfigFormSchema } from "../../schemas";
import {
  IComparisonScoringConfig,
  ICreateComparisonScoringConfig,
  IEditComparisonScoringConfig,
} from "../../types/Scoring.type";
import {
  createComparisonScoringConfig,
  updateComparisonScoringConfig,
  getComparisonScoringConfigByRule,
} from "../../services/scoringService";
import {
  comparisonScoringConfigDefaults,
  scoringSeverityOptions,
  customFactorTargetOptions,
} from "../../services/constants";
import { toast } from "react-toastify";
import {
  isComparisonCategoryWeightTotalValid,
  isScoringSubmitDisabled,
} from "../../utils/scoringConfigFormValidation";
import { getRuleDetail } from "../../services/rulesService";

interface IComparisonScoringConfigFormProps {
  ruleId: number;
  existingConfig: IComparisonScoringConfig | null;
  onSaveSuccess?: () => void;
  readOnly?: boolean;
  onValuesChange?: (values: any) => void;
  onValidityChange?: (isValid: boolean) => void;
  hideCreateButton?: boolean;
}

const ComparisonScoringConfigForm: React.FC<
  IComparisonScoringConfigFormProps
> = ({
  ruleId,
  existingConfig,
  onSaveSuccess,
  readOnly,
  onValuesChange,
  onValidityChange,
  hideCreateButton,
}) => {
  const [hasChanges, setHasChanges] = useState(false);
  const [currentConfig, setCurrentConfig] =
    useState<IComparisonScoringConfig | null>(existingConfig);
  const [severityMappings, setSeverityMappings] = useState<
    Record<string, string>
  >(existingConfig?.severity_mappings || {});
  const [checkNameOptions, setCheckNameOptions] = useState<string[]>([]);

  useEffect(() => {
    setCurrentConfig(existingConfig);
  }, [existingConfig]);

  useEffect(() => {
    setSeverityMappings(existingConfig?.severity_mappings || {});
  }, [existingConfig]);

  const isEditMode = currentConfig !== null && currentConfig.id > 0;

  useEffect(() => {
    const fetchCheckNames = async () => {
      try {
        const ruleData = await getRuleDetail(ruleId);
        const domainNames = (
          ruleData?.rule_schema?.domain_comparison_rules || []
        ).map((r: any) => r.name);
        const customNames = (
          ruleData?.rule_schema?.custom_comparison_rules || []
        ).map((r: any) => r.name);
        setCheckNameOptions([...domainNames, ...customNames]);
      } catch (err) {
        console.error("Failed to load check names:", err);
      }
    };
    if (ruleId) fetchCheckNames();
  }, [ruleId]);

  const formik = useFormik({
    initialValues: {
      blend_ratio:
        currentConfig?.blend_ratio ??
        comparisonScoringConfigDefaults.blend_ratio,
      category_weight_missing:
        currentConfig?.category_weight_missing ??
        comparisonScoringConfigDefaults.category_weight_missing,
      category_weight_mismatch:
        currentConfig?.category_weight_mismatch ??
        comparisonScoringConfigDefaults.category_weight_mismatch,
      category_weight_validation:
        currentConfig?.category_weight_validation ??
        comparisonScoringConfigDefaults.category_weight_validation,
      missing_factor:
        currentConfig?.missing_factor ??
        comparisonScoringConfigDefaults.missing_factor,
      mismatch_factor_low:
        currentConfig?.mismatch_factor_low ??
        comparisonScoringConfigDefaults.mismatch_factor_low,
      mismatch_factor_medium:
        currentConfig?.mismatch_factor_medium ??
        comparisonScoringConfigDefaults.mismatch_factor_medium,
      mismatch_factor_high:
        currentConfig?.mismatch_factor_high ??
        comparisonScoringConfigDefaults.mismatch_factor_high,
      custom_factor_sql: currentConfig?.custom_factor_sql || "",
      custom_factor_target: currentConfig?.custom_factor_target || "",
      comment: "",
    },
    validationSchema: comparisonScoringConfigFormSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const cleanMappings = Object.fromEntries(
          Object.entries(severityMappings).filter(
            ([k]) => k !== "" && !k.startsWith("__new_"),
          ),
        );

        if (isEditMode) {
          const editBody: IEditComparisonScoringConfig = {
            blend_ratio: values.blend_ratio,
            category_weight_missing: values.category_weight_missing,
            category_weight_mismatch: values.category_weight_mismatch,
            category_weight_validation: values.category_weight_validation,
            missing_factor: values.missing_factor,
            mismatch_factor_low: values.mismatch_factor_low,
            mismatch_factor_medium: values.mismatch_factor_medium,
            mismatch_factor_high: values.mismatch_factor_high,
            custom_factor_sql: values.custom_factor_sql || null,
            custom_factor_target: values.custom_factor_target || null,
            severity_mappings: cleanMappings,
            comment: values.comment,
          };
          await updateComparisonScoringConfig(currentConfig!.id, editBody);
          toast.success("Comparison scoring config updated successfully.");
        } else {
          const createBody: ICreateComparisonScoringConfig = {
            rule_id: ruleId,
            blend_ratio: values.blend_ratio,
            category_weight_missing: values.category_weight_missing,
            category_weight_mismatch: values.category_weight_mismatch,
            category_weight_validation: values.category_weight_validation,
            missing_factor: values.missing_factor,
            mismatch_factor_low: values.mismatch_factor_low,
            mismatch_factor_medium: values.mismatch_factor_medium,
            mismatch_factor_high: values.mismatch_factor_high,
            custom_factor_sql: values.custom_factor_sql || null,
            custom_factor_target: values.custom_factor_target || null,
            severity_mappings: cleanMappings,
          };
          await createComparisonScoringConfig(createBody);
          toast.success("Comparison scoring config created successfully.");
        }
        // Refetch the config so form switches to edit mode
        if (ruleId && ruleId > 0) {
          const refreshed = await getComparisonScoringConfigByRule(ruleId);
          setCurrentConfig(refreshed);
          if (refreshed?.severity_mappings) {
            setSeverityMappings(refreshed.severity_mappings);
          }
        }
        setHasChanges(false);
        if (onSaveSuccess) onSaveSuccess();
      } catch (error) {
        console.error(error);
        toast.error("Failed to save comparison scoring config.");
      }
    },
  });

  // Track changes by comparing current values to initial values
  useEffect(() => {
    const initial = formik.initialValues;
    const current = formik.values;
    const valuesChanged =
      initial.blend_ratio !== current.blend_ratio ||
      initial.category_weight_missing !== current.category_weight_missing ||
      initial.category_weight_mismatch !== current.category_weight_mismatch ||
      initial.category_weight_validation !==
        current.category_weight_validation ||
      initial.missing_factor !== current.missing_factor ||
      initial.mismatch_factor_low !== current.mismatch_factor_low ||
      initial.mismatch_factor_medium !== current.mismatch_factor_medium ||
      initial.mismatch_factor_high !== current.mismatch_factor_high ||
      initial.custom_factor_sql !== current.custom_factor_sql ||
      initial.custom_factor_target !== current.custom_factor_target;

    // Also check if severity mappings changed from the existing config
    const initialMappings = currentConfig?.severity_mappings || {};
    const mappingsChanged =
      JSON.stringify(initialMappings) !==
      JSON.stringify(
        Object.fromEntries(
          Object.entries(severityMappings).filter(
            ([k]) => k !== "" && !k.startsWith("__new_"),
          ),
        ),
      );

    setHasChanges(valuesChanged || mappingsChanged);
  }, [formik.values, severityMappings, currentConfig]);

  useEffect(() => {
    if (onValuesChange) {
      onValuesChange({
        ...formik.values,
        severity_mappings: severityMappings,
      });
    }
  }, [formik.values, severityMappings]);

  // Category weight sum validation display.
  // Guard with Number() conversion: formik v2 stores '' (empty string) for
  // type="number" inputs when parseFloat returns NaN (e.g. when the user
  // types "." or presses Backspace leaving an invalid intermediate value).
  // Without the guard, direct addition of '' + number produces a string via
  // JS string-concatenation, and the subsequent .toFixed(2) call throws a
  // TypeError that crashes the screen (DQ-8556).
  const weightsSum =
    Number(formik.values.category_weight_missing || 0) +
    Number(formik.values.category_weight_mismatch || 0) +
    Number(formik.values.category_weight_validation || 0);
  const weightsSumValid = isComparisonCategoryWeightTotalValid(formik.values);

  useEffect(() => {
    onValidityChange?.(formik.isValid && weightsSumValid);
  }, [formik.isValid, onValidityChange, weightsSumValid]);

  /**
   * Custom onChange handler for numeric <input type="number"> fields.
   *
   * DQ-8556: when the browser reports e.target.value="" for an intermediate
   * decimal (e.g. "0."), we must NOT immediately commit "" so that the browser
   * input buffer can keep showing the partial value while the user continues
   * typing. NaN-producing non-empty strings (impossible for type="number" in
   * practice) are also left untouched.
   *
   * DQ-8521: when the user genuinely clears the field (all digits deleted,
   * e.target.value === ""), commit "" so that React re-renders with an empty
   * controlled value instead of snapping back to the previous Formik value,
   * and so that the Yup required-field validation fires on blur.
   */
  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value === "") {
      formik.setFieldValue(name, "");
      return;
    }
    const v = parseFloat(value);
    if (!isNaN(v)) {
      formik.setFieldValue(name, v);
    }
    // Non-empty NaN (intermediate state left by the browser): leave Formik
    // untouched so the browser input buffer persists — DQ-8556 fix.
  };

  const handleNumericBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    formik.handleBlur(e);
    if (isNaN(parseFloat(e.target.value))) {
      // Commit '' so Yup required-field validation fires after the user
      // has deliberately cleared a field and moved focus away.
      formik.setFieldValue(e.target.name, "");
    }
  };

  const handleResetToDefaults = () => {
    formik.setValues({
      blend_ratio: comparisonScoringConfigDefaults.blend_ratio,
      category_weight_missing:
        comparisonScoringConfigDefaults.category_weight_missing,
      category_weight_mismatch:
        comparisonScoringConfigDefaults.category_weight_mismatch,
      category_weight_validation:
        comparisonScoringConfigDefaults.category_weight_validation,
      missing_factor: comparisonScoringConfigDefaults.missing_factor,
      mismatch_factor_low: comparisonScoringConfigDefaults.mismatch_factor_low,
      mismatch_factor_medium:
        comparisonScoringConfigDefaults.mismatch_factor_medium,
      mismatch_factor_high:
        comparisonScoringConfigDefaults.mismatch_factor_high,
      custom_factor_sql: "",
      custom_factor_target: "",
      comment: formik.values.comment,
    });
    setSeverityMappings({});
  };

  // Severity mapping handlers
  const entries = Object.entries(severityMappings);

  const newMappingCounter = useRef(0);
  const handleAddMapping = () => {
    const tempKey = `__new_${newMappingCounter.current++}`;
    setSeverityMappings((prev) => ({ ...prev, [tempKey]: "medium" }));
  };

  const handleRemoveMapping = (checkName: string) => {
    setSeverityMappings((prev) => {
      const next = { ...prev };
      delete next[checkName];
      return next;
    });
  };

  const handleUpdateCheckName = (oldName: string, newName: string) => {
    if (newName === oldName) return;
    setSeverityMappings((prev) => {
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([k, v]) => {
        next[k === oldName ? newName : k] = v;
      });
      return next;
    });
  };

  const handleUpdateSeverity = (checkName: string, severity: string) => {
    setSeverityMappings((prev) => ({ ...prev, [checkName]: severity }));
  };

  const handleSaveClick = () => {
    const hasEmptyOrTempKey = Object.keys(severityMappings).some(
      (k) => k === "" || k.startsWith("__new_"),
    );
    if (hasEmptyOrTempKey) {
      toast.error(
        "Please select a check name for all severity mappings before saving.",
      );
      return;
    }
    formik.handleSubmit();
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 700 }}>
        Comparison Scoring Configuration
      </Typography>

      {/* Category Weights Section */}
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Category Weights
      </Typography>
      <Grid
        container
        rowSpacing={2.5}
        columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
        sx={{ mb: 2 }}
      >
        <Grid item xs={12} md={3}>
          <label className="label-text">Missing Weight</label>
          <TextField
            name="category_weight_missing"
            type="number"
            fullWidth
            inputProps={{ step: 0.05 }}
            InputProps={{ readOnly: readOnly }}
            disabled={readOnly}
            value={formik.values.category_weight_missing}
            onChange={handleNumericChange}
            onBlur={handleNumericBlur}
            className={`form-control ${
              formik.touched.category_weight_missing &&
              formik.errors.category_weight_missing
                ? "has-error"
                : ""
            }`}
            error={
              formik.touched.category_weight_missing &&
              Boolean(formik.errors.category_weight_missing)
            }
            helperText={
              formik.touched.category_weight_missing &&
              formik.errors.category_weight_missing
            }
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <label className="label-text">Mismatched Weight</label>
          <TextField
            name="category_weight_mismatch"
            type="number"
            fullWidth
            inputProps={{ step: 0.05 }}
            InputProps={{ readOnly: readOnly }}
            disabled={readOnly}
            value={formik.values.category_weight_mismatch}
            onChange={handleNumericChange}
            onBlur={handleNumericBlur}
            className={`form-control ${
              formik.touched.category_weight_mismatch &&
              formik.errors.category_weight_mismatch
                ? "has-error"
                : ""
            }`}
            error={
              formik.touched.category_weight_mismatch &&
              Boolean(formik.errors.category_weight_mismatch)
            }
            helperText={
              formik.touched.category_weight_mismatch &&
              formik.errors.category_weight_mismatch
            }
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <label className="label-text">Validation Weight</label>
          <TextField
            name="category_weight_validation"
            type="number"
            fullWidth
            inputProps={{ step: 0.05 }}
            InputProps={{ readOnly: readOnly }}
            disabled={readOnly}
            value={formik.values.category_weight_validation}
            onChange={handleNumericChange}
            onBlur={handleNumericBlur}
            className={`form-control ${
              formik.touched.category_weight_validation &&
              formik.errors.category_weight_validation
                ? "has-error"
                : ""
            }`}
            error={
              formik.touched.category_weight_validation &&
              Boolean(formik.errors.category_weight_validation)
            }
            helperText={
              formik.touched.category_weight_validation &&
              formik.errors.category_weight_validation
            }
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
          sx={{ display: "flex", alignItems: "center" }}
        >
          {weightsSumValid ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: "success.main",
              }}
            >
              <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">
                Sum: {weightsSum.toFixed(2)}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: "error.main",
              }}
            >
              <ErrorIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">
                Sum: {weightsSum.toFixed(2)} (must be 1.0)
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Missing Records Section */}
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Missing Records
      </Typography>
      <Grid
        container
        rowSpacing={2.5}
        columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
        sx={{ mb: 2 }}
      >
        <Grid item xs={12} md={4}>
          <label className="label-text">Blend Ratio</label>
          <TextField
            name="blend_ratio"
            type="number"
            fullWidth
            inputProps={{ step: 0.05, min: 0, max: 1 }}
            InputProps={{ readOnly: readOnly }}
            disabled={readOnly}
            value={formik.values.blend_ratio}
            onChange={handleNumericChange}
            onBlur={handleNumericBlur}
            className={`form-control ${
              formik.touched.blend_ratio && formik.errors.blend_ratio
                ? "has-error"
                : ""
            }`}
            error={
              formik.touched.blend_ratio && Boolean(formik.errors.blend_ratio)
            }
            helperText={formik.touched.blend_ratio && formik.errors.blend_ratio}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <label className="label-text">Missing Factor</label>
          <TextField
            name="missing_factor"
            type="number"
            fullWidth
            inputProps={{ step: 0.1, min: 0 }}
            InputProps={{ readOnly: readOnly }}
            disabled={readOnly}
            value={formik.values.missing_factor}
            onChange={handleNumericChange}
            onBlur={handleNumericBlur}
            className={`form-control ${
              formik.touched.missing_factor && formik.errors.missing_factor
                ? "has-error"
                : ""
            }`}
            error={
              formik.touched.missing_factor &&
              Boolean(formik.errors.missing_factor)
            }
            helperText={
              formik.touched.missing_factor && formik.errors.missing_factor
            }
          />
        </Grid>
      </Grid>

      {/* Mismatched Records Section */}
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Mismatched Records
      </Typography>
      <Grid
        container
        rowSpacing={2.5}
        columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
        sx={{ mb: 2 }}
      >
        <Grid item xs={12} md={4}>
          <label className="label-text">Factor (Low)</label>
          <TextField
            name="mismatch_factor_low"
            type="number"
            fullWidth
            inputProps={{ step: 0.05 }}
            InputProps={{ readOnly: readOnly }}
            disabled={readOnly}
            value={formik.values.mismatch_factor_low}
            onChange={handleNumericChange}
            onBlur={handleNumericBlur}
            className={`form-control ${
              formik.errors.mismatch_factor_low ? "has-error" : ""
            }`}
            error={Boolean(formik.errors.mismatch_factor_low)}
            helperText={formik.errors.mismatch_factor_low || " "}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <label className="label-text">Factor (Medium)</label>
          <TextField
            name="mismatch_factor_medium"
            type="number"
            fullWidth
            inputProps={{ step: 0.05 }}
            InputProps={{ readOnly: readOnly }}
            disabled={readOnly}
            value={formik.values.mismatch_factor_medium}
            onChange={handleNumericChange}
            onBlur={handleNumericBlur}
            className={`form-control ${
              formik.errors.mismatch_factor_medium ? "has-error" : ""
            }`}
            error={Boolean(formik.errors.mismatch_factor_medium)}
            helperText={formik.errors.mismatch_factor_medium || " "}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <label className="label-text">Factor (High)</label>
          <TextField
            name="mismatch_factor_high"
            type="number"
            fullWidth
            inputProps={{ step: 0.05 }}
            InputProps={{ readOnly: readOnly }}
            disabled={readOnly}
            value={formik.values.mismatch_factor_high}
            onChange={handleNumericChange}
            onBlur={handleNumericBlur}
            className={`form-control ${
              formik.errors.mismatch_factor_high ? "has-error" : ""
            }`}
            error={Boolean(formik.errors.mismatch_factor_high)}
            helperText={formik.errors.mismatch_factor_high || " "}
          />
        </Grid>
      </Grid>

      {/* Severity Mappings Section */}
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        Severity Mappings (check_name to severity)
      </Typography>
      <Box
        sx={{
          border: "1px solid var(--table-border)",
          borderRadius: "4px",
          mb: 2,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Check Name</strong>
                </TableCell>
                <TableCell sx={{ width: 160 }}>
                  <strong>Severity</strong>
                </TableCell>
                <TableCell align="center" sx={{ width: 60 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map(([checkName, severity]) => {
                const usedNames = Object.keys(severityMappings).filter(
                  (n) => n !== checkName,
                );
                const availableOptions = checkNameOptions.filter(
                  (opt) => !usedNames.includes(opt),
                );
                return (
                  <TableRow key={checkName}>
                    <TableCell>
                      <Autocomplete
                        value={
                          checkName.startsWith("__new_")
                            ? null
                            : checkName || null
                        }
                        options={availableOptions}
                        disabled={readOnly}
                        onChange={(_e, newValue) =>
                          handleUpdateCheckName(checkName, newValue || "")
                        }
                        className="form-control-autocomplete dropdown-padding-1"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            className="form-control"
                            placeholder="Select check name"
                          />
                        )}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ width: 160 }}>
                      <Select
                        fullWidth
                        value={severity}
                        disabled={readOnly}
                        onChange={(e) =>
                          handleUpdateSeverity(checkName, e.target.value)
                        }
                        size="small"
                      >
                        {scoringSeverityOptions.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell align="center">
                      {!readOnly && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveMapping(checkName)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {!readOnly && (
            <Box sx={{ p: 1, borderTop: "1px solid var(--table-border)" }}>
              <Button
                size="small"
                className="btn-orange btn-sm"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddMapping}
              >
                Add Mapping
              </Button>
            </Box>
          )}
        </TableContainer>
      </Box>

      {/* Custom SQL Override (Collapsible) */}
      <Accordion
        sx={{
          mb: 2,
          border: "1px solid var(--table-border)",
          boxShadow: "none",
          "&:before": { display: "none" },
          borderRadius: "4px !important",
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Custom Factor SQL Override (Advanced)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid
            container
            rowSpacing={2.5}
            columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
          >
            <Grid item xs={12} md={4}>
              <label className="label-text">Apply Factor To</label>
              <Select
                fullWidth
                name="custom_factor_target"
                value={formik.values.custom_factor_target}
                onChange={formik.handleChange}
                size="small"
                displayEmpty
                disabled={readOnly}
                className="form-control-autocomplete"
              >
                <MenuItem value="">
                  <em>None (disabled)</em>
                </MenuItem>
                {customFactorTargetOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12}>
              <label className="label-text">
                Custom Factor SQL{" "}
                <span style={{ fontWeight: 400, opacity: 0.7 }}>
                  (must return a single float value)
                </span>
              </label>
              <TextField
                name="custom_factor_sql"
                fullWidth
                value={formik.values.custom_factor_sql}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-control"
                multiline
                rows={4}
                placeholder="SELECT CAST(custom_value AS FLOAT) FROM custom_factors WHERE ..."
                disabled={readOnly || !formik.values.custom_factor_target}
                InputProps={{ readOnly: readOnly }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Comment (edit mode only) */}
      {isEditMode && !readOnly && (
        <Box sx={{ mb: 2 }}>
          <label className="label-text">Change Comment</label>
          <TextField
            name="comment"
            fullWidth
            value={formik.values.comment}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="form-control"
            multiline
            rows={2}
            disabled={readOnly}
            InputProps={{ readOnly: readOnly }}
          />
        </Box>
      )}

      {/* Action Buttons */}
      {!readOnly && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            columnGap: 1.5,
            mt: 2,
          }}
        >
          <Button
            variant="contained"
            className="btn-orange btn-dark"
            disabled={formik.isSubmitting}
            onClick={handleResetToDefaults}
            startIcon={<RestoreIcon />}
          >
            Reset to Defaults
          </Button>
          {!hideCreateButton && (
            <Button
              variant="contained"
              className="btn-orange"
              disabled={isScoringSubmitDisabled({
                isSubmitting: formik.isSubmitting,
                isValid: formik.isValid,
                isEditMode,
                hasChanges,
                hasAdditionalInvalidState: !weightsSumValid,
              })}
              startIcon={
                formik.isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveOutlinedIcon />
                )
              }
              onClick={handleSaveClick}
            >
              {formik.isSubmitting ? "Saving..." : "Save"}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ComparisonScoringConfigForm;
